import { ChaveDeConversa } from "@/ui/ChaveDeConversa";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  formatPoints, platePoints, pointsLabel, resolveConfig, mealWeight, eventOpen,
  restamEstimativas, APPS_DE_DELIVERY, CANAIS, tipoDaMissao,
  type AppDeDelivery, type Canal, type ChallengeConfig,
  desafiosQueAceitamODia, lerPalpite,
} from "@nhomi/core";
import { nomeDaSessao, useSession } from "@/lib/auth";
import { watchChallenge, watchMyChallenges, type Challenge } from "@/data/challenges";
import { createPlate, dayKey, watchMealsOfDay } from "@/data/plates";
import { watchPlaces, type Place } from "@/data/places";
import { watchUsoDeHoje } from "@/data/kcal";
import { useMe, usePlano } from "@/lib/plano";
import { CotaDeCaloria } from "@/ui/CotaDeCaloria";
import type { Coords } from "@/lib/places";
import type { Progresso } from "@/lib/upload";
import { capturar, ImagemInvalida, prepararFotos, type FotoEscolhida, type Recorte } from "@/lib/image";
import { Recortador } from "@/ui/Recortador";
import { pegarFotosEntregues, temFotosEntregues } from "@/lib/compartilhado";
import { Avatar, Spinner, TopBar } from "@/ui/primitives";
import { descreverFalha, FalhaCard, type Falha } from "@/ui/Falha";
import { PlacePicker, type LugarEscolhido } from "@/ui/PlacePicker";
import {
  IconCalendar, IconCamera, IconCheckSmall, IconClock, IconPencil, IconPin, IconPlus,
} from "@/ui/icons";
import { Camera, Conferir, type ErroDaFoto, type FotoPronta } from "./Camera";
import { Toast } from "@/ui/Toast";
import { LogoDoApp } from "@/ui/LogoDoApp";
import { PedidoDaMissao } from "@/ui/Missoes";

/* ============================================================
   Estimar a caloria junto com o prato

   Antes a estimativa era um botão na tela do prato, e o que acontecia é
   que a pessoa publicava e ia embora: o prato ficava sem caloria, e a
   galera não tinha o que chutar. O gesto certo — "quero saber quantas
   calorias isto tem" — já estava dado no momento de publicar; o botão
   depois só cobrava o mesmo gesto de novo, numa tela que ela nem sempre
   voltava a abrir.

   Por isso o interruptor NASCE LIGADO. Um padrão desligado devolveria o
   problema inteiro pra quem esquece, que é justamente quem ele existe
   pra atender.

   A escolha fica no APARELHO, não no perfil. É preferência de uso, e
   quem desliga pra poupar cota não quer desligar de novo a cada prato —
   mas também não vale uma escrita no Firestore e uma leitura a mais em
   toda abertura do app. Sem `localStorage` (aba anônima, armazenamento
   bloqueado), o padrão volta a valer, que é o comportamento certo.
   ============================================================ */
const ESTIMAR_AO_PUBLICAR = "nhomi.estimarAoPublicar";

/** Qualquer coisa que não seja um "0" gravado conta como ligado. */
const lerPreferenciaDeEstimar = (): boolean => {
  try { return localStorage.getItem(ESTIMAR_AO_PUBLICAR) !== "0"; } catch { return true; }
};

/** "12:34" a partir de uma data. */
const hhmm = (d: Date) => {
  const p = (n: number) => String(n).padStart(2, "0");
  return `${p(d.getHours())}:${p(d.getMinutes())}`;
};

export function Compose() {
  const [toast, setToast] = useState<string | null>(null)
  const { cid = "" } = useParams();
  const navigate = useNavigate();
  const [busca] = useSearchParams();
  const { user } = useSession();

  const [challenge, setChallenge] = useState<Challenge | null>(null);
  const [jaHoje, setJaHoje] = useState<Set<string>>(new Set());

  // A câmera é o primeiro passo, não um campo do formulário. Publicar é o
  // gesto de fotografar o prato; o resto é preenchimento.
  const [passo, setPasso] = useState<"camera" | "conferir" | "formulario">("camera");
  const [fotos, setFotos] = useState<FotoPronta[]>([]);
  /* Prato SEM FOTO (set/2026): escolhido na câmera, custa no placar e
     troca a foto pela descrição obrigatória — é o que a IA lê. */
  const [semFoto, setSemFoto] = useState(false);

  /* Veio de fora? Isto precisa ser sabido no PRIMEIRO render, não num
     efeito: a câmera pede `getUserMedia` assim que monta, e quem chegou
     aqui compartilhando uma foto do WhatsApp receberia um pedido de
     permissão de câmera do nada — antes de ver qualquer coisa do app.
     `temFotosEntregues` só olha, então dá pra chamar durante o render. */
  const [deFora] = useState(temFotosEntregues);
  const [preparando, setPreparando] = useState(false);
  const [erroDaFoto, setErroDaFoto] = useState<ErroDaFoto | null>(null);

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [homemade, setHomemade] = useState(false);
  /* Onde foi comprado. Opcional, e só aparece com "Comprei". */
  const [canal, setCanal] = useState<Canal | null>(null);
  const [conversa, setConversa] = useState(true);
  const [appDelivery, setAppDelivery] = useState<AppDeDelivery | null>(null);
  const [mealId, setMealId] = useState("");
  const [cuisineId, setCuisineId] = useState("");
  const [price, setPrice] = useState("");
  const [place, setPlace] = useState("");
  const [coords, setCoords] = useState<Coords | null>(null);
  const [abrindoLocal, setAbrindoLocal] = useState(false);

  /* ---------- a caloria, decidida aqui e não depois ---------- */
  const { config: configDoApp, plano, assinante, tetoDeCaloriaHoje } = usePlano();
  const me = useMe();
  const [estimarJa, setEstimarJa] = useState(lerPreferenciaDeEstimar);
  const [usadasHoje, setUsadasHoje] = useState(0);

  /* O mesmo contador que a tela do prato lê. Ele é pra MOSTRAR: quem
     recusa a estimativa acima do teto é a função, numa transação. */
  const uid = user?.uid;
  useEffect(() => {
    if (!uid) return;
    return watchUsoDeHoje(uid, setUsadasHoje);
  }, [uid]);

  const restam = restamEstimativas(configDoApp, plano, usadasHoje);
  const semCota = restam === 0;
  /* O interruptor aparece desligado sem cota, mas a PREFERÊNCIA não é
     apagada: amanhã ela volta ligada sozinha, sem a pessoa ter que
     lembrar de religar o que ela nunca desligou. */
  const vaiEstimar = estimarJa && !semCota;

  function trocarEstimar(valor: boolean) {
    setEstimarJa(valor);
    try {
      localStorage.setItem(ESTIMAR_AO_PUBLICAR, valor ? "1" : "0");
    } catch { /* sem armazenamento: vale só nesta tela, e tudo bem */ }
  }

  /* O Chrome restaura valores de formulário entre sessões, e o campo de
     data chegava preenchido com o dia em que o formulário foi aberto pela
     última vez — foi assim que pratos publicados hoje foram gravados com
     data de ontem. O estado nasce do relógio a cada montagem. */
  const agora = useMemo(() => new Date(), []);
  const hojeKey = dayKey(agora);
  const [day, setDay] = useState(hojeKey);
  const [time, setTime] = useState(hhmm(agora));

  /* Os outros desafios da pessoa, pra publicar o mesmo prato em mais de
     um. Quem está em três grupos comia uma vez e digitava três vezes — na
     prática não digitava, e os outros grupos ficavam vazios. */
  const [meusDesafios, setMeusDesafios] = useState<Challenge[]>([]);
  const [replicas, setReplicas] = useState<Set<string>>(new Set());

  const [enviando, setEnviando] = useState(false);
  const [progresso, setProgresso] = useState<Progresso | null>(null);
  const [falha, setFalha] = useState<Falha | null>(null);


  useEffect(() => {
    if (!toast) return;

    const timer = window.setTimeout(() => setToast(null), 2600);
    return () => window.clearTimeout(timer);
  }, [toast]);
  useEffect(() => watchChallenge(cid, setChallenge), [cid]);

  /* Os lugares que o grupo já marcou. `watchPlaces` já devolve ordenado
     por visitas, então os seis primeiros são os que mais têm chance de
     ser o de hoje. Só entram os que têm ponto no mapa: um chip que não
     traz `coords` não resolve o problema que ele existe pra resolver. */
  const [lugares, setLugares] = useState<Place[]>([]);
  useEffect(() => watchPlaces(cid, setLugares), [cid]);
  const lugaresConhecidos = useMemo(
    () => lugares.filter((l) => l.coords).slice(0, 6),
    [lugares],
  );
  useEffect(() => {
    if (!user) return;
    return watchMealsOfDay(cid, user.uid, day, setJaHoje);
  }, [cid, user, day]);
  useEffect(() => {
    if (!user) return;
    return watchMyChallenges(user.uid, setMeusDesafios);
  }, [user]);

  /* As prévias pertencem a esta tela: são criadas quando a foto é
     preparada e revogadas quando ela sai ou a tela morre. Criá-las num
     efeito com `[fotos]` como dependência revogaria as antigas a cada
     foto nova, e a lista inteira piscaria. */
  const fotosRef = useRef<FotoPronta[]>([]);
  fotosRef.current = fotos;
  useEffect(() => () => {
    for (const f of fotosRef.current) URL.revokeObjectURL(f.url);
  }, []);

  /**
   * Reduz a foto AGORA e guarda o resultado em memória.
   *
   * O `File` da câmera do Android aponta pra um arquivo temporário que é
   * reciclado quando a câmera abre de novo — segurá-lo até o publish
   * fazia as três fotos virarem miniaturas quebradas e um "não consegui
   * ler essa imagem" no fim do formulário.
   */
  /* A FILA É O CONSERTO DE UM BUG DE ORDEM.

     A galeria entrega várias fotos de uma vez, e cada uma passa por um
     `await` que reduz a imagem. Sem fila, as três reduziam ao mesmo
     tempo e cada uma se acrescentava QUANDO TERMINAVA — ou seja, na
     ordem do tamanho do arquivo, não na ordem em que a pessoa escolheu.

     A foto mais pesada caía por último. Como a primeira da lista é a
     capa, e a capa é o que a IA analisa, escolher o prato primeiro e
     receber a estimativa de outra foto era o comportamento normal.

     Encadear as promessas garante ordem de chegada = ordem da lista,
     sem depender de quem chama nem de quantas vezes. */
  const fila = useRef<Promise<void>>(Promise.resolve());

  /* O arquivo da última foto que não deu certo, pro botão de tentar de
     novo. A releitura parte do `File` original, não da promessa que já
     falhou: quando a causa é a galeria ainda baixando a foto da nuvem, é
     a segunda leitura que encontra os bytes lá. */
  const recusada = useRef<File | null>(null);

  function receberFoto(escolha: FotoEscolhida) {
    fila.current = fila.current.then(() => processarFoto(escolha));
  }

  /* A foto que veio de outro app entra pela MESMA porta da galeria: vira
     `FotoEscolhida` e cai na fila de preparo. Assim ela ganha de graça a
     ordem preservada, o teto de fotos, a redução e a mensagem de erro que
     a câmera já tem — e não existe um segundo caminho de publicar pra
     ficar velho sozinho.

     `capturar()` aqui é barato: os bytes já estão em memória, vindos do
     cache do worker. Não há `content://` pra escapar como na galeria, mas
     passar pelo mesmo lugar é o que evita duas leituras diferentes.

     A trava é por causa do `StrictMode`, que monta tudo duas vezes em
     desenvolvimento — sem ela a mesma foto entraria duas vezes na lista. */
  const jaPeguei = useRef(false);
  useEffect(() => {
    if (jaPeguei.current) return;
    jaPeguei.current = true;
    for (const file of pegarFotosEntregues()) receberFoto(capturar(file));
  }, []);

  function tentarFotoDeNovo() {
    const file = recusada.current;
    if (file) receberFoto(capturar(file));
  }

  async function processarFoto(escolha: FotoEscolhida) {
    setPreparando(true);
    setErroDaFoto(null);
    setFalha(null);
    try {
      const pronta = await prepararFotos(escolha);
      recusada.current = null;
      // Chegou foto: não é mais prato sem foto, e não paga o custo.
      setSemFoto(false);
      setFotos((atuais) => [...atuais, { ...pronta, url: URL.createObjectURL(pronta.photo), origem: escolha }]);
      setPasso("conferir");
    } catch (err) {
      recusada.current = escolha.file;
      const detalhe = descreverFalha(err, "preparar a foto");
      /* O porquê inteiro fica no console, como no resto do app: a tela
         diz o que dá pra fazer, e o código curto é o que faz um print de
         suporte valer alguma coisa. */
      console.warn("[nhomi] foto recusada", detalhe);
      setErroDaFoto({
        recado: err instanceof ImagemInvalida
          ? err.message
          : `Não consegui usar essa foto: ${detalhe.recado}`,
        codigo: detalhe.codigo,
      });
    } finally {
      setPreparando(false);
    }
  }

  /**
   * Põe a foto escolhida na frente.
   *
   * A primeira da lista é a capa, e a capa é a única que a IA analisa —
   * o servidor lê `photoPath`, que é `photos[0]`. Sem um jeito de
   * escolher, quem posta o prato junto de uma foto do lugar fica na mão
   * da ordem em que selecionou.
   */
  function definirCapa(i: number) {
    setFotos((atuais) => {
      if (i <= 0 || i >= atuais.length) return atuais;
      const nova = [...atuais];
      const [escolhida] = nova.splice(i, 1);
      return escolhida ? [escolhida, ...nova] : atuais;
    });
  }

  /* ---------- cortar ----------
     O corte parte da foto ORIGINAL (`origem`), não da versão já
     reduzida: cortar a de 1080 px e reduzir de novo perderia nitidez a
     cada corte. A posição na lista não muda — a capa continua capa. */
  const [cortando, setCortando] = useState<{ i: number; foto: Blob } | null>(null);
  async function abrirCorte(i: number) {
    const origem = fotos[i]?.origem;
    if (!origem) return;
    try { setCortando({ i, foto: await origem.bytes }); } catch { /* a foto já tinha aberto; não acontece */ }
  }
  async function cortarFoto(i: number, recorte: Recorte) {
    const alvo = fotos[i];
    if (!alvo?.origem) return;
    setCortando(null);
    setPreparando(true);
    try {
      const origem = { ...alvo.origem, recorte };
      const pronta = await prepararFotos(origem);
      setFotos((atuais) => atuais.map((f, j) => {
        if (j !== i) return f;
        URL.revokeObjectURL(f.url);
        return { ...pronta, url: URL.createObjectURL(pronta.photo), origem };
      }));
    } catch (err) {
      setToast(descreverFalha(err, "cortar a foto").recado);
    } finally {
      setPreparando(false);
    }
  }

  function removerFoto(i: number) {
    setFotos((atuais) => {
      const morta = atuais[i];
      if (morta) URL.revokeObjectURL(morta.url);
      return atuais.filter((_, j) => j !== i);
    });
  }

  /* ---------- pontos ---------- */

  const config: ChallengeConfig = useMemo(() => resolveConfig(challenge), [challenge]);

  /* ---------- a refeição que o lembrete já sabia ----------

     O lembrete das 12h sabe que é o almoço e mandava a pessoa para cá sem
     contar: ela via os chips em branco e escolhia de novo o que o aviso
     acabara de dizer. Agora a url carrega `?refeicao=almoco`.

     Roda num efeito e não no estado inicial porque a lista de refeições
     vem do DESAFIO, que chega depois — validar contra `config.meals` é o
     que impede um link com lixo (ou com a refeição de um desafio que
     apagou o slot) de marcar um chip que não existe.

     Só a PRIMEIRA vez, e só com o campo ainda vazio: quem já tocou num
     chip escolheu, e sobrescrever uma escolha da pessoa por causa de um
     parâmetro de url é a mesma falta do campo de data que restaurava o
     dia errado. */
  const refeicaoDaUrl = busca.get("refeicao") ?? "";
  const refeicaoAplicada = useRef(false);
  useEffect(() => {
    if (refeicaoAplicada.current || !refeicaoDaUrl || mealId) return;
    if (!config.meals.some((m) => m.id === refeicaoDaUrl)) return;
    refeicaoAplicada.current = true;
    setMealId(refeicaoDaUrl);
  }, [refeicaoDaUrl, mealId, config]);

  const repetida = !!mealId && day === hojeKey && jaHoje.has(mealId);
  const custoSemFoto = semFoto ? config.rules.semFotoCusto : 0;
  const pts = useCallback(
    (caseiro: boolean) =>
      platePoints(config, { homemade: caseiro, mealId: mealId || null, repeated: repetida }) - custoSemFoto,
    [config, mealId, repetida, custoSemFoto],
  );
  const vale = pts(homemade);
  const nomeRefeicao = config.meals.find((m) => m.id === mealId)?.label ?? "";
  const eventosAbertos = config.events.filter((ev) => eventOpen(ev, hojeKey));

  /* Onde mais este prato PODE aparecer. O recorte usa o dia do prato, não
     o de hoje: quem posta o almoço de ontem quer os desafios que estavam
     rolando ontem. Desafio encerrado não recebe prato novo. */
  const outrosDesafios = useMemo(
    () => desafiosQueAceitamODia(meusDesafios, cid, day),
    [meusDesafios, cid, day],
  );

  /* Mudou o dia, some desafio que não vale mais para ele — e a marca
     precisa sumir junto. Sem isso a pessoa marcaria um desafio, mudaria a
     data, e publicaria em um lugar que a tela já não mostra mais. */
  useEffect(() => {
    setReplicas((atual) => {
      const validos = new Set(outrosDesafios.map((d) => d.id));
      const podado = new Set([...atual].filter((id) => validos.has(id)));
      return podado.size === atual.size ? atual : podado;
    });
  }, [outrosDesafios]);

  /* Quanto este prato vai valer, dito ANTES de publicar. Sem isso a pessoa
     marca "Cozinhei" num whey, vê "2 pontos" escrito no botão, e só
     descobre no placar que a refeição valia 0,5 — ou zero, por ser a
     segunda igual do dia. */
  const aviso = (() => {
    if (repetida) {
      return (
        <>
          Você já postou <b>{nomeRefeicao}</b> hoje. Pode publicar do mesmo
          jeito — a galera chuta preço e caloria —, mas este vale {pointsLabel(vale)}.
        </>
      );
    }
    if (mealId && mealWeight(config, mealId) !== 1) {
      // Os dois valores, sempre: dizer só o do botão ativo faz a pessoa
      // achar que o outro botão vale o de tabela.
      return (
        <>
          <b>{nomeRefeicao}</b> vale diferente neste desafio:{" "}
          {pointsLabel(pts(false))} comprado, {pointsLabel(pts(true))} cozinhado.
        </>
      );
    }
    return null;
  })();

  /* ---------- publicar ---------- */

  async function publicar() {
    if (!user || (!fotos.length && !semFoto)) return;
    const nome = title.trim();
    if (!nome) {
      setToast("Dá um nome pro prato");
      // setFalha({ codigo: "sem-título", recado: "Dá um nome pro prato.", etapa: "conferir o formulário" });
      return;
    }
    // Sem foto, a descrição é o prato: é ela que a IA e a galera leem.
    // Cinco letras é o mesmo piso da regra do Firestore.
    if (semFoto && description.trim().length < 5) {
      setToast("Sem foto, descreve o que comeu e quanto");
      return;
    }
    // O mesmo teto da regra do Firestore: R$ 5.000 é erro de digitação.
    const precoLido = price.trim() ? lerPalpite("price", price) : null;
    if (precoLido && !precoLido.ok) {
      setToast(precoLido.erro);
      return;
    }
    if (!mealId) {
      setToast("Bota uma tag de refeição");
      // setFalha({ codigo: "sem-refeição", recado: "Escolhe a refeição do prato.", etapa: "preencher o formulário" });
      return;
    }

    setEnviando(true);
    setFalha(null);
    setProgresso(null);
    // Saber a etapa muda o diagnóstico: preparar é problema de foto,
    // gravar é problema de regra ou de rede.
    let etapa = semFoto ? "gravar o prato" : "preparar e enviar a foto";
    try {
      const at = new Date(`${day}T${time || "12:00"}:00`);
      const pid = await createPlate(cid, {
        uid: user.uid,
        name: me?.name ?? nomeDaSessao(user),
        photo: me?.photo ?? "",
        email: user.email ?? "",
      }, {
        title: nome,
        description: description.trim(),
        mealId: mealId || null,
        cuisineId: cuisineId || null,
        homemade,
        price: precoLido?.ok ? precoLido.valor : null,
        place: place.trim(),
        coords,
        canal: homemade ? null : canal,
        appDelivery: !homemade && canal === "delivery" ? appDelivery : null,
        fechado: !conversa,
        fotos: semFoto ? [] : fotos,
        at,
        // Só conta como escolha deliberada se a pessoa realmente mexeu.
        dateEdited: day !== hojeKey || time !== hhmm(agora),
        replicas: [...replicas],
      }, (p) => {
        // Foto inteira enviada: o que falta é o documento. Saber em qual
        // das duas parou muda o diagnóstico — foto é Storage, documento
        // é regra do Firestore.
        if (p.fracao >= 1) etapa = "gravar o prato";
        setProgresso(p);
      });
      /* O pedido de estimativa viaja com a navegação, e quem CHAMA é a
         tela do prato — ela é que tem o "olhando a foto…" e o lugar do
         erro. Ver o porquê inteiro em `routes/Plate.tsx`. */
      navigate(`/c/${cid}/p/${pid}`, {
        replace: true,
        state: vaiEstimar ? { estimarJa: true } : null,
      });
    } catch (err) {
      setEnviando(false);
      setProgresso(null);
      setFalha(descreverFalha(err, etapa));
    }
  }

  /* ---------- passos 1 e 2: a foto ---------- */

  /* Chegou de fora e a foto ainda está sendo reduzida. São décimos de
     segundo, mas eles não podem ser a CÂMERA: abrir a câmera pra quem
     acabou de escolher uma foto pronta é pedir permissão sem motivo e
     mostrar a tela errada. Se o preparo falhar, `erroDaFoto` derruba
     esta condição e a câmera aparece com o recado e o "tentar de novo" —
     que é onde a pessoa pode escolher outra foto. */
  if (deFora && !fotos.length && !erroDaFoto && !semFoto) {
    return (
      <main className="screen center">
        <Spinner label="Preparando a foto" />
        <p className="hint-row center">Preparando a foto…</p>
      </main>
    );
  }

  // Sem foto não existe formulário — nem por rota estranha, nem por
  // "trocar a foto" no meio do caminho. A exceção é a escolha explícita
  // de postar sem foto, que a câmera oferece com o custo escrito.
  if (passo === "camera" || (!fotos.length && !semFoto)) {
    return (
      <Camera
        onEscolher={receberFoto}
        onSemFoto={() => { setSemFoto(true); setPasso("formulario"); }}
        custoSemFoto={pointsLabel(config.rules.semFotoCusto)}
        preparando={preparando}
        erro={erroDaFoto}
        onTentarDeNovo={erroDaFoto ? tentarFotoDeNovo : undefined}
        // Sair da câmera com fotos já tiradas volta pra conferência, não
        // joga tudo fora: quem toca no X depois de fotografar quer voltar,
        // não perder o trabalho.
        onSair={() => (fotos.length ? setPasso("conferir") : navigate(`/c/${cid}`))}
      />
    );
  }

  if (passo === "conferir" && fotos.length) {
    return (
      <>
      <Conferir
        fotos={fotos}
        onCortar={(i) => void abrirCorte(i)}
        onOk={() => setPasso("formulario")}
        onMais={() => setPasso("camera")}
        onRemover={removerFoto}
        onCapa={definirCapa}
        onSair={() => navigate(`/c/${cid}`)}
      />
      {cortando && (
        <Recortador foto={cortando.foto} titulo="Cortar a foto"
          onCancelar={() => setCortando(null)}
          onPronto={(r) => void cortarFoto(cortando.i, r)} />
      )}
      </>
    );
  }

  /* ---------- passo 3: o formulário ---------- */

  const pct = Math.round((progresso?.fracao ?? 0) * 100);

  const fecharLocal = (v: LugarEscolhido | null) => {
    setAbrindoLocal(false);
    if (!v) return;
    setPlace(v.name);
    setCoords(v.coords);
  };

  return (
    <main className="screen">
      <TopBar
        title="Novo prato"
        onBack={() => setPasso("camera")}
        action={
          <button className="topbar-action" onClick={publicar} disabled={enviando}>
            {enviando ? "Enviando…" : "Publicar"}
          </button>
        }
      />

      <div className="screen-body">
        <div className="compose-head">
          <div className="compose-side">
            <div className="avatar-stack">
              <Avatar name={me?.name ?? nomeDaSessao(user)} photo={me?.photo} size="sm" />
            </div>
            <div className="cs-text">Em {challenge?.name || "1 desafio"}</div>
          </div>

          <div className="compose-photo">
            {fotos[0]
              ? <img src={fotos[0].url} alt="Prévia do prato" />
              : <div className="compose-photo-vazia"><IconCamera size={30} /></div>}
            {fotos.length > 1 && <span className="compose-conta">{fotos.length} fotos</span>}
            <button className="edit-btn"
              onClick={() => setPasso(fotos.length ? "conferir" : "camera")}
              aria-label={fotos.length ? "Ver as fotos" : "Tirar uma foto"}>
              <IconPencil size={20} />
            </button>
          </div>

          <div className="compose-side">
            <div className={`valid-badge ${fotos.length ? "" : "off"}`}><IconCheckSmall size={18} /></div>
            <div>{fotos.length ? "Válido" : "Sem foto"}</div>
          </div>
        </div>

        {falha && <FalhaCard falha={falha} />}

        {enviando && (
          <div className="progresso" role="progressbar"
            aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
            <div className="progresso-barra" style={{ width: `${Math.max(pct, 4)}%` }} />
            <span>{semFoto ? "Publicando…" : progresso ? `Enviando a foto — ${pct}%` : "Preparando a foto…"}</span>
          </div>
        )}

        <div className="card">
          <label className="field">
            <input value={title} onChange={(e) => setTitle(e.target.value)}
              placeholder="Título" maxLength={80} autoComplete="off" />
          </label>
          <label className="field">
            <textarea value={description} onChange={(e) => setDescription(e.target.value)}
              placeholder={semFoto
                ? "O que você comeu e quanto — ex.: 2 conchas de arroz, 1 concha de feijão, 1 filé de frango grelhado"
                : "Descrição (opcional)"}
              maxLength={600} />
          </label>
          {semFoto && (
            <div className="hint-row">
              Sem foto, este prato vale <b>{pointsLabel(config.rules.semFotoCusto)} a menos</b>, e a
              descrição é obrigatória: é ela que a IA lê pra estimar as calorias. Quanto mais
              detalhe de quantidade, melhor a estimativa.
            </div>
          )}
        </div>

        <div className="row-2">
          <div className="card"><div className="field-inline">
            <div className="field-body">
              <span className="field-label">Dia</span>
              <input type="date" value={day} autoComplete="off"
                onChange={(e) => setDay(e.target.value)} />
            </div>
            <span className="ico"><IconCalendar size={22} /></span>
          </div></div>
          <div className="card"><div className="field-inline">
            <div className="field-body">
              <span className="field-label">Hora</span>
              <input type="time" value={time} autoComplete="off"
                onChange={(e) => setTime(e.target.value)} />
            </div>
            <span className="ico"><IconClock size={22} /></span>
          </div></div>
        </div>

        <div className="card">
          <div className="toggle-row">
            <button className={`toggle-opt ${homemade ? "" : "active"}`} onClick={() => setHomemade(false)}>
              🛒 Comprei<span>{pointsLabel(pts(false))}</span>
            </button>
            <button className={`toggle-opt ${homemade ? "active" : ""}`} onClick={() => setHomemade(true)}>
              👨‍🍳 Cozinhei<span>{pointsLabel(pts(true))}</span>
            </button>
          </div>
          {aviso && <div className="hint-row">{aviso}</div>}

          {/* Onde foi comprado. Opcional: tocar de novo desmarca. Separa
              comer no lugar, retirar e pedir por app — e qual app. */}
          {!homemade && (
            <>
              <div className="field-label chips-titulo canal-titulo">
                Como foi? <span className="opcional">opcional</span>
              </div>
              <div className="chip-wrap">
                {CANAIS.map((c) => (
                  <button key={c.id} className={`chip ${canal === c.id ? "active" : ""}`}
                    onClick={() => {
                      setCanal(canal === c.id ? null : c.id);
                      if (c.id !== "delivery" || canal === c.id) setAppDelivery(null);
                    }}>
                    {c.emoji} {c.rotulo}
                  </button>
                ))}
              </div>
              {canal === "delivery" && (
                <div className="chip-wrap canal-apps">
                  {APPS_DE_DELIVERY.map((a) => (
                    <button key={a.id} className={`chip ${appDelivery === a.id ? "active" : ""}`}
                      onClick={() => setAppDelivery(appDelivery === a.id ? null : a.id)}>
                      <LogoDoApp app={a.id} /> {a.rotulo}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {/* Onde foi.

            O seletor continua existindo e continua bom — o que mudou é
            que ele deixou de ser o ÚNICO caminho. Abrir uma tela cheia,
            buscar numa base que tem pouco restaurante e torcer pra achar
            é preço alto demais pra um campo opcional, e o resultado era
            um mapa de grupo quase sempre vazio.

            Gente come no mesmo lugar de novo. Os lugares que o grupo já
            marcou vêm da coleção `places`, que a função já mantém — e o
            documento guarda as `coords`, então um toque num chip traz o
            ponto no mapa de carona, sem rede e sem busca. */}
        <div className="card">
          <div className="field-label chips-titulo">
            Onde foi? <span className="opcional">opcional</span>
          </div>
          <div className="chip-wrap">
            {lugaresConhecidos.map((l) => (
              <button key={l.id}
                className={`chip ${place === l.name ? "active" : ""}`}
                onClick={() => {
                  // Tocar no que já está escolhido desmarca, como nos
                  // chips de refeição e de selo logo abaixo.
                  const mesmo = place === l.name;
                  setPlace(mesmo ? "" : l.name);
                  setCoords(mesmo ? null : l.coords);
                }}>
                <IconPin size={15} /> {l.name}
              </button>
            ))}
            {/* Lugar digitado que ainda não é do grupo continua visível:
                sem isto ele sumiria da tela ao ser escolhido no seletor. */}
            {place && !lugaresConhecidos.some((l) => l.name === place) && (
              <button className="chip active" onClick={() => { setPlace(""); setCoords(null); }}>
                <IconPin size={15} /> {place}
              </button>
            )}
            <button className="chip chip-vazado" onClick={() => setAbrindoLocal(true)}>
              <IconPlus size={15} /> {lugaresConhecidos.length ? "Outro lugar" : "Escolher lugar"}
            </button>
          </div>
          <div className="hint-row">
            {coords
              ? "📍 Marcado no mapa — entra no guia do grupo."
              : place
                ? "Sem ponto no mapa: entra na lista do guia, não no mapa."
                : lugaresConhecidos.length
                  ? "Lugares que a galera já marcou. Um toque e o ponto no mapa vem junto."
                  : "O primeiro lugar marcado começa o guia do grupo."}
          </div>
        </div>

        <div className="card">
          <div className="field-inline">
            <span className="ico emoji">💸</span>
            <div className="field-body">
              <span className="field-label">Quanto custou? (opcional)</span>
              <input type="number" inputMode="decimal" min="0" max="5000" step="0.01" placeholder="R$ —"
                value={price} onChange={(e) => setPrice(e.target.value)} />
            </div>
          </div>
          <div className="hint-row">Fica escondido: a galera chuta o preço antes de ver.</div>
        </div>

        <div className="card">
          <div className="field-label chips-titulo">Refeição</div>
          <div className="chip-wrap">
            {config.meals.map((m) => (
              <button key={m.id} className={`chip ${m.id === mealId ? "active" : ""}`}
                onClick={() => setMealId(m.id === mealId ? "" : m.id)}>
                {m.emoji || "🍽️"} {m.label}
                {m.weight !== 1 && <span className="chip-peso">×{formatPoints(m.weight)}</span>}
              </button>
            ))}
          </div>
        </div>

        <div className="card">
          <div className="field-label chips-titulo">Selo · carimba o passaporte</div>
          <div className="chip-wrap scroll">
            {config.cuisines.map((c) => (
              <button key={c.id} className={`chip ${c.id === cuisineId ? "active" : ""}`}
                onClick={() => setCuisineId(c.id === cuisineId ? "" : c.id)}>
                {c.emoji} {c.label}
              </button>
            ))}
          </div>
        </div>

        {eventosAbertos.length > 0 && (
          <div className="card">
            <div className="field-label chips-titulo">Missões valendo</div>
            {eventosAbertos.map((ev) => (
              <div key={ev.id} className="evento-linha">
                <span className="evento-nome">
                  {ev.emoji || "🎯"} {ev.name}
                  <span className="evento-pede"><PedidoDaMissao e={ev} config={config} /></span>
                </span>
                <span className="evento-premio">+{pointsLabel(ev.bonus)}</span>
              </div>
            ))}
            {eventosAbertos.some((ev) => tipoDaMissao(ev) === "colecao") && (
              <div className="hint-row">Nas de coleção, escolha um selo da lista pra avançar.</div>
            )}
          </div>
        )}

        {outrosDesafios.length > 0 && (
          <div className="card">
            <div className="field-label chips-titulo">Publicar também em</div>
            <div className="chip-wrap">
              <button
                className={`chip ${replicas.size === outrosDesafios.length ? "active" : ""}`}
                onClick={() => setReplicas(
                  replicas.size === outrosDesafios.length
                    ? new Set()
                    : new Set(outrosDesafios.map((d) => d.id)),
                )}>
                ✅ Todos
              </button>
              {outrosDesafios.map((d) => (
                <button key={d.id} className={`chip ${replicas.has(d.id) ? "active" : ""}`}
                  onClick={() => setReplicas((atual) => {
                    const proximo = new Set(atual);
                    if (!proximo.delete(d.id)) proximo.add(d.id);
                    return proximo;
                  })}>
                  {d.name}
                </button>
              ))}
            </div>
            <div className="hint-row">
              {replicas.size
                ? `O mesmo prato entra em mais ${replicas.size} ${replicas.size === 1 ? "desafio" : "desafios"
                }, com a foto, o preço e a caloria. Cada um pontua pelas regras dele.`
                : "Comeu uma vez e está em mais de um desafio? Marque aqui e não precisa postar de novo."}
            </div>
          </div>
        )}

        {/* Junto do "publicar também em" de propósito: os dois são o que
            acontece DEPOIS de tocar em Publicar, e é a última coisa que
            a pessoa lê antes de tocar. */}
        <div className="card">
          <div className="switch-row">
            <div className="switch-corpo">
              <div className="switch-titulo">🔥 Estimar calorias ao publicar</div>
              <div className="switch-sub">
                {semCota
                  ? "Sem estimativa hoje — dá pra pedir amanhã, na tela do prato."
                  : assinante
                    ? `A IA ${semFoto ? "lê a descrição" : "olha a foto"} assim que o prato sobe.`
                    : /* O número aparece pra quem não assina porque pra
                         essa pessoa ele muda a decisão: cinco por dia
                         acaba. Pra quem assina, contar 97 de 100 é ruído. */
                    <>
                      A IA {semFoto ? "lê a descrição" : "olha a foto"} assim que o prato sobe.{" "}
                      <b>{restam === 1 ? "Resta 1 estimativa" : `Restam ${restam} estimativas`}</b>
                      {" "}de {tetoDeCaloriaHoje} hoje.
                    </>}
              </div>
            </div>
            <button
              className={`switch ${vaiEstimar ? "on" : ""}`}
              disabled={semCota}
              onClick={() => trocarEstimar(!estimarJa)}
              role="switch"
              aria-checked={vaiEstimar}
              aria-label="Estimar calorias ao publicar">
              <span />
            </button>
          </div>

          {/* Cota acabar não é erro: é o produto funcionando como
              combinado. Por isso o recado explica e oferece a saída, em
              vez de deixar um interruptor ligado que ia falhar. */}
          {semCota && <CotaDeCaloria contexto="publicar" />}
        </div>

        <ChaveDeConversa ligado={conversa} aoMudar={setConversa} />

        <div className="gap" />
      </div>

      {abrindoLocal && (
        <PlacePicker inicial={{ name: place, coords }} onFechar={fecharLocal} />
      )}

      {toast && <Toast text={toast} />}
    </main>
  );
}
